import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/contexts/AuthContext";
import { DemoPreviewProvider } from "@/contexts/DemoPreviewContext";
import DemoPreviewBadge from "@/components/admin/DemoPreviewBadge";
import { DemoLockdownBanner } from "@/components/DemoLockdownBanner";
import { LangProvider } from "@/i18n";
import ProtectedRoute from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import VerifyEmailPending from "@/pages/VerifyEmailPending";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ChangePassword from "@/pages/ChangePassword";
import UserDashboard from "@/pages/user/UserDashboard";
import MyRacketsPage from "@/pages/user/MyRacketsPage";
import MyRewardsPage from "@/pages/user/MyRewardsPage";
import MyEquipmentPage from "@/pages/user/MyEquipmentPage";
import MyReservationPage from "@/pages/user/MyReservationPage";
import MyQrPage from "@/pages/user/MyQrPage";
import MyRacketLabOpsPage from "@/pages/user/MyRacketLabOpsPage";
import MyQuestionnairesPage from "@/pages/user/MyQuestionnairesPage";
import MySettings from "@/pages/user/MySettings";
import TestAvatars from "@/pages/TestAvatars";
import Questionnaire from "@/pages/user/Questionnaire";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminQuestionnaires from "@/pages/admin/AdminQuestionnaires";
import QuestionnaireBuilder from "@/pages/admin/QuestionnaireBuilder";
import AdminInterviewer from "@/pages/admin/AdminInterviewer";
import AdminCampaigns from "@/pages/admin/AdminCampaigns";
import AdminConsent from "@/pages/admin/AdminConsent";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminImport from "@/pages/admin/AdminImport";
import AdminResponses from "@/pages/admin/AdminResponses";
import AdminTesters from "@/pages/admin/AdminTesters";
import AdminStatsAdvanced from "@/pages/admin/AdminStatsAdvanced";
import AdminReferrals from "@/pages/admin/AdminReferrals";
import AdminFraud from "@/pages/admin/AdminFraud";
import AdminReferralCampaigns from "@/pages/admin/AdminReferralCampaigns";
import AdminAdSlots from "@/pages/admin/AdminAdSlots";
import AdminMarketplaceRequests from "@/pages/admin/AdminMarketplaceRequests";
import AdminVendors from "@/pages/admin/AdminVendors";
import AdminVendorOffers from "@/pages/admin/AdminVendorOffers";
import QaCommissions from "@/pages/admin/QaCommissions";
import DemoActivityLog from "@/pages/admin/DemoActivityLog";
import MarketplaceDemand from "@/pages/admin/MarketplaceDemand";
import WishlistAdoptionModal from "@/components/wlp/WishlistAdoptionModal";
import RacketLabTickets from "@/pages/racket_lab/RacketLabTickets";
import RacketLabCalendar from "@/pages/racket_lab/RacketLabCalendar";
import RacketLabHome from "@/pages/racket_lab/RacketLabHome";
import RacketLabSettings from "@/pages/racket_lab/RacketLabSettings";
import RacketLabLanding from "@/pages/racket_lab/RacketLabLanding";
import RLAdminControlledProfiles from "@/pages/racket_lab/admin/RLAdminControlledProfiles";
// Sub-sprint A — Racket Lab admin shell (read-only, admin-only).
import RLAdminDashboard from "@/pages/racket_lab/admin/RLAdminDashboard";
import RLAdminShops from "@/pages/racket_lab/admin/RLAdminShops";
import RLAdminShopDetail from "@/pages/racket_lab/admin/RLAdminShopDetail";
import RLAdminTickets from "@/pages/racket_lab/admin/RLAdminTickets";
import RLAdminMyTickets from "@/pages/racket_lab/admin/RLAdminMyTickets";
import RLAdminCalendar from "@/pages/racket_lab/admin/RLAdminCalendar";
import RLAdminTicketQrPrint from "@/pages/racket_lab/admin/RLAdminTicketQrPrint";
import AdminCPCohort from "@/pages/admin/AdminCPCohort";
import AdminGearPreview from "@/pages/admin/AdminGearPreview";
import AdminGearStoreAccess from "@/pages/admin/AdminGearStoreAccess";
import AdminGearOfferOpportunities from "@/pages/admin/AdminGearOfferOpportunities";
// Sub-sprint B — write surfaces (admin-only).
import RLAdminOperators from "@/pages/racket_lab/admin/RLAdminOperators";
import AdminCatalogImportReview from "@/pages/admin/AdminCatalogImportReview";
import RLAdminShifts from "@/pages/racket_lab/admin/RLAdminShifts";
import RLAdminCapabilities from "@/pages/racket_lab/admin/RLAdminCapabilities";
import RLAdminTicketDetail from "@/pages/racket_lab/admin/RLAdminTicketDetail";
import RLAdminQrTokens from "@/pages/racket_lab/admin/RLAdminQrTokens";
import RLAdminQrTokenDetail from "@/pages/racket_lab/admin/RLAdminQrTokenDetail";
import RLAdminResolveViewer from "@/pages/racket_lab/admin/RLAdminResolveViewer";
import RLAdminEnvelopes from "@/pages/racket_lab/admin/RLAdminEnvelopes";
import RLAdminEnvelopeDetail from "@/pages/racket_lab/admin/RLAdminEnvelopeDetail";
import RLAdminMockOutbox from "@/pages/racket_lab/admin/RLAdminMockOutbox";
import RLAdminMockOutboxItem from "@/pages/racket_lab/admin/RLAdminMockOutboxItem";
import RLAdminOpsToolkit from "@/pages/racket_lab/admin/RLAdminOpsToolkit";
import RLAdminOpsPromote from "@/pages/racket_lab/admin/RLAdminOpsPromote";
import RLAdminOpsGating from "@/pages/racket_lab/admin/RLAdminOpsGating";
import RLAdminOpsRedisHealth from "@/pages/racket_lab/admin/RLAdminOpsRedisHealth";
import RLAdminOpsScanAuditOverview from "@/pages/racket_lab/admin/RLAdminOpsScanAuditOverview";
import RLAdminOpsLegacy from "@/pages/racket_lab/admin/RLAdminOpsLegacy";
import RLAdminOpsLegacyRunReport from "@/pages/racket_lab/admin/RLAdminOpsLegacyRunReport";
import RLAdminOpsRetention from "@/pages/racket_lab/admin/RLAdminOpsRetention";
import RLAdminOpsSeedHelper from "@/pages/racket_lab/admin/RLAdminOpsSeedHelper";
import RLAdminInventory from "@/pages/racket_lab/admin/RLAdminInventory";
import RLSectionGuard from "@/modules/racket_lab_admin/RLSectionGuard";

// SS-8/CP-1.3 — wraps a Racket Lab page with its RBAC section guard.
const RLS = (section, el) => <RLSectionGuard section={section}>{el}</RLSectionGuard>;
import ReferralLanding from "@/pages/ReferralLanding";
import Unsubscribe from "@/pages/Unsubscribe";
import MyReferrals from "@/pages/user/MyReferrals";
import MyCalendar from "@/pages/user/MyCalendar";
import MyMeetingTypes from "@/pages/user/MyMeetingTypes";
import MyBookings from "@/pages/user/MyBookings";
import BookingByToken from "@/pages/BookingByToken";
import PublicBookingPage from "@/pages/PublicBookingPage";
import MyWishlists from "@/pages/user/MyWishlists";
import MyMarketplaceRequests from "@/pages/user/MyMarketplaceRequests";
import AppsHub from "@/pages/apps/AppsHub";
import AppComingSoon from "@/pages/apps/AppComingSoon";
import MyAppsSettings from "@/pages/user/MyAppsSettings";
import AppErrorBoundary from "@/components/AppErrorBoundary";

// Stable role arrays — declared once at module scope so React never sees a
// new array reference on re-renders (avoids needless ProtectedRoute work).
const ROLES_ANY = ["user", "staff", "admin", "qa_tester", "qa_manager", "institutional_observer"];
const ROLES_ADMIN = ["admin", "staff", "qa_manager", "institutional_observer", "qa_tester"];

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LangProvider>
          <AuthProvider>
            <DemoPreviewProvider>
              <Toaster position="top-right" richColors />
              <DemoLockdownBanner />
              <DemoPreviewBadge />
              <AppErrorBoundary>
              {/* WLP Adoption B3.1 — mounted globally for authenticated
                  users; component is self-gated by server-side
                  eligibility (admin/staff/QA roles return show_modal=false
                  so nothing renders). Hidden on /app/settings to avoid
                  duplicating the in-page banner. */}
              <WishlistAdoptionModal />
              <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/verify-email-pending" element={<VerifyEmailPending />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              {/* A19-P1.3 — public referral flows */}
              <Route path="/r/:token" element={<ReferralLanding />} />
              <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
              {/* Block D Calendar MVP — public booking flows. */}
              <Route path="/book/:hostSlug/:mtId" element={<PublicBookingPage />} />
              <Route path="/booking/:token" element={<BookingByToken />} />
              {/* TEMP — owl avatar variants comparison page for José.
                  Public route so the URL is screenshot-shareable on
                  WhatsApp. Delete together with TestAvatars.jsx after
                  the default variant is approved. */}
              <Route path="/test-avatars" element={<TestAvatars />} />
              {/* TEAM OS Hub — public app catalog (v1) */}
              <Route path="/apps" element={<AppsHub />} />
              <Route path="/apps/questionnaires" element={<Landing />} />
              <Route path="/apps/:slug" element={<AppComingSoon />} />
              {/* RACKET-LAB-FRONT-1 — public landing for Racket Lab (Jun 2026). */}
              <Route path="/racket-lab" element={<RacketLabLanding />} />
              <Route path="/apps/racket-lab" element={<RacketLabLanding />} />
              <Route path="/change-password" element={
                <ProtectedRoute roles={ROLES_ANY}><ChangePassword /></ProtectedRoute>
              } />

              <Route path="/app" element={
                <ProtectedRoute roles={ROLES_ANY}><UserDashboard /></ProtectedRoute>
              } />
              {/* DASHBOARD-SIDEMENU-PROFILES-1-FIX1 (Feb 2026) — standalone
                  Mis raquetas and Mis recompensas sections, lifted out of
                  the dashboard so the side menu has dedicated destinations. */}
              <Route path="/app/my-rackets" element={
                <ProtectedRoute roles={ROLES_ANY}><MyRacketsPage /></ProtectedRoute>
              } />
              <Route path="/app/my-rewards" element={
                <ProtectedRoute roles={ROLES_ANY}><MyRewardsPage /></ProtectedRoute>
              } />
              {/* DASHBOARD-SIDEMENU-PROFILES-1-FIX2 (Feb 2026) — Racket Lab
                  submenu landing pages. Player-only at the page level
                  (each page redirects non-Player profiles back to /app). */}
              <Route path="/app/racket-lab/equipment" element={
                <ProtectedRoute roles={ROLES_ANY}><MyEquipmentPage /></ProtectedRoute>
              } />
              <Route path="/app/racket-lab/reserva" element={
                <ProtectedRoute roles={ROLES_ANY}><MyReservationPage /></ProtectedRoute>
              } />
              <Route path="/app/racket-lab/qr" element={
                <ProtectedRoute roles={ROLES_ANY}><MyQrPage /></ProtectedRoute>
              } />
              {/* FINAL-DEMO-UX-I18N-EQUIPMENT-QA-1 — "Mis cuestionarios"
                  placeholder. Personal questionnaires history surface
                  is not backed by a customer-facing endpoint yet. */}
              <Route path="/app/questionnaires" element={
                <ProtectedRoute roles={ROLES_ANY}><MyQuestionnairesPage /></ProtectedRoute>
              } />
              {/* RACKET-LAB-PROFILES-FINAL-PREVIEW-ALIGN-1 — placeholder
                  "Vista operativa" for ops-track profiles
                  (racket_technician, shop_manager, operator, stringer)
                  + staff with admin console access. Page itself
                  self-redirects non-allowed profiles to /app. */}
              <Route path="/app/racket-lab/ops" element={
                <ProtectedRoute roles={ROLES_ANY}><MyRacketLabOpsPage /></ProtectedRoute>
              } />
              <Route path="/app/referrals" element={
                <ProtectedRoute roles={ROLES_ANY}><MyReferrals /></ProtectedRoute>
              } />
              <Route path="/app/calendar" element={
                <ProtectedRoute roles={ROLES_ANY}><MyCalendar /></ProtectedRoute>
              } />
              <Route path="/app/calendar/meeting-types" element={
                <ProtectedRoute roles={ROLES_ANY}><MyMeetingTypes /></ProtectedRoute>
              } />
              <Route path="/host/bookings" element={
                <ProtectedRoute roles={ROLES_ANY}><MyBookings /></ProtectedRoute>
              } />
              <Route path="/app/wishlists" element={
                <ProtectedRoute roles={ROLES_ANY}><MyWishlists /></ProtectedRoute>
              } />
              <Route path="/app/marketplace-requests" element={
                <ProtectedRoute roles={ROLES_ANY}><MyMarketplaceRequests /></ProtectedRoute>
              } />
              <Route path="/app/settings" element={
                <ProtectedRoute roles={ROLES_ANY}><MySettings /></ProtectedRoute>
              } />
              {/* F4 HUB Foundation — Apps settings (Feb 2026). Only mounted
                  when REACT_APP_HUB_NAV_ENABLED=true (Preview-only). */}
              {process.env.REACT_APP_HUB_NAV_ENABLED === "true" && (
                <Route path="/app/settings/apps" element={
                  <ProtectedRoute roles={ROLES_ANY}><MyAppsSettings /></ProtectedRoute>
                } />
              )}
              {/* Racket Lab R3 — Service Tickets (Preview only).
                  Page is self-gated by backend feature flag; if
                  RACKET_LAB_ENABLED=false the page renders the
                  "disabled" panel and never calls the API. */}
              <Route path="/app/racket-lab/tickets" element={
                <ProtectedRoute roles={ROLES_ANY}><RacketLabTickets /></ProtectedRoute>
              } />
              <Route path="/app/racket-lab/calendar" element={
                <ProtectedRoute roles={ROLES_ANY}><RacketLabCalendar /></ProtectedRoute>
              } />
              {/* RACKET-LAB-FRONT-1 — authed Racket Lab app shell. */}
              <Route path="/app/racket-lab" element={
                <ProtectedRoute roles={ROLES_ANY}><RacketLabHome /></ProtectedRoute>
              } />
              <Route path="/app/racket-lab/settings" element={
                <ProtectedRoute roles={ROLES_ANY}><RacketLabSettings /></ProtectedRoute>
              } />
              <Route path="/questionnaire/:id" element={
                <ProtectedRoute roles={ROLES_ANY}><Questionnaire /></ProtectedRoute>
              } />

              <Route path="/admin" element={
                <ProtectedRoute roles={ROLES_ADMIN}><AdminLayout /></ProtectedRoute>
              }>
                <Route index element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="controlled-production" element={<AdminCPCohort />} />
                <Route path="gear/preview" element={<AdminGearPreview />} />
                <Route path="gear/store-access" element={<AdminGearStoreAccess />} />
                <Route path="gear/offer-opportunities" element={<AdminGearOfferOpportunities />} />
                <Route path="questionnaires" element={<AdminQuestionnaires />} />
                <Route path="questionnaires/new" element={<QuestionnaireBuilder />} />
                <Route path="questionnaires/:id" element={<QuestionnaireBuilder />} />
                <Route path="interviewer" element={<AdminInterviewer />} />
                <Route path="campaigns" element={<AdminCampaigns />} />
                <Route path="consent" element={<AdminConsent />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="import" element={<AdminImport />} />
                <Route path="responses" element={<AdminResponses />} />
                <Route path="stats-advanced" element={<AdminStatsAdvanced />} />
                <Route path="testers" element={<AdminTesters />} />
                <Route path="referrals" element={<AdminReferrals />} />
                <Route path="referral-campaigns" element={<AdminReferralCampaigns />} />
                <Route path="ad-slots" element={<AdminAdSlots />} />
                <Route path="marketplace-requests" element={<AdminMarketplaceRequests />} />
                <Route path="vendors" element={<AdminVendors />} />
                <Route path="offers" element={<AdminVendorOffers />} />
                <Route path="fraud" element={<AdminFraud />} />
                <Route path="qa-commissions" element={<QaCommissions />} />
                <Route path="demo-activity-log" element={<DemoActivityLog />} />
                <Route path="marketplace/demand" element={<MarketplaceDemand />} />
                {/* CATALOG-2B (Feb 2026) — admin/staff-only read-only
                    review of catalog import jobs (dry-run + applied +
                    rolled_back). No apply / rollback / import buttons
                    in this slice. */}
                <Route path="catalog/import-review" element={<AdminCatalogImportReview />} />
                {/* Racket Lab Admin Shell — Sub-sprint A (read-only,
                    admin-only). Mount path: /admin/racket-lab/*. */}
                <Route path="racket-lab" element={RLS("dashboard", <RLAdminDashboard />)} />
                <Route path="racket-lab/workforce/shops" element={RLS("shops", <RLAdminShops />)} />
                <Route path="racket-lab/workforce/shops/:id" element={RLS("shops", <RLAdminShopDetail />)} />
                <Route path="racket-lab/workforce/operators" element={RLS("operators", <RLAdminOperators />)} />
                <Route path="racket-lab/workforce/shifts" element={RLS("shifts", <RLAdminShifts />)} />
                <Route path="racket-lab/workforce/capabilities" element={RLS("capabilities", <RLAdminCapabilities />)} />
                <Route path="racket-lab/inventory" element={RLS("inventory", <RLAdminInventory />)} />
                <Route path="racket-lab/tickets" element={RLS("tickets", <RLAdminTickets />)} />
                <Route path="racket-lab/my-tickets" element={RLS("my_tickets", <RLAdminMyTickets />)} />
                <Route path="racket-lab/calendar" element={RLS("calendar", <RLAdminCalendar />)} />
                <Route path="racket-lab/tickets/:id/qr-print" element={RLS("tickets", <RLAdminTicketQrPrint />)} />
                <Route path="racket-lab/tickets/:id" element={RLS(["tickets", "my_tickets"], <RLAdminTicketDetail />)} />
                {/* Sub-sprint C — QR tokens + internal resolve + scan audit. */}
                <Route path="racket-lab/qr-tokens" element={RLS("qr_tokens", <RLAdminQrTokens />)} />
                <Route path="racket-lab/qr-tokens/resolve" element={RLS("resolve", <RLAdminResolveViewer />)} />
                <Route path="racket-lab/qr-tokens/:id" element={RLS("qr_tokens", <RLAdminQrTokenDetail />)} />
                {/* Sub-sprint C — Notifications inspect (envelopes + NP-R1 mock outbox). */}
                <Route path="racket-lab/notifications/envelopes" element={RLS("envelopes", <RLAdminEnvelopes />)} />
                <Route path="racket-lab/notifications/envelopes/:id" element={RLS("envelopes", <RLAdminEnvelopeDetail />)} />
                <Route path="racket-lab/notifications/mock-outbox" element={RLS("mock_outbox", <RLAdminMockOutbox />)} />
                <Route path="racket-lab/notifications/mock-outbox/:id" element={RLS("mock_outbox", <RLAdminMockOutboxItem />)} />
                {/* Sub-sprint D-α — Ops Toolkit base. */}
                <Route path="racket-lab/ops" element={RLS("ops", <RLAdminOpsToolkit />)} />
                <Route path="racket-lab/ops/promote" element={RLS("ops", <RLAdminOpsPromote />)} />
                <Route path="racket-lab/ops/gating" element={RLS("ops", <RLAdminOpsGating />)} />
                <Route path="racket-lab/ops/redis" element={RLS("ops", <RLAdminOpsRedisHealth />)} />
                <Route path="racket-lab/ops/scan-audit" element={RLS("ops", <RLAdminOpsScanAuditOverview />)} />
                <Route path="racket-lab/ops/legacy" element={RLS("ops", <RLAdminOpsLegacy />)} />
                <Route path="racket-lab/ops/legacy/runs/:runId" element={RLS("ops", <RLAdminOpsLegacyRunReport />)} />
                <Route path="racket-lab/ops/retention" element={RLS("ops", <RLAdminOpsRetention />)} />
                <Route path="racket-lab/ops/seed-helper" element={RLS("ops", <RLAdminOpsSeedHelper />)} />
                {/* RACKET-LAB-FRONT-1 — Controlled QA profiles directory (admin/staff/qa_manager). */}
                <Route path="racket-lab/controlled-profiles" element={<RLAdminControlledProfiles />} />
              </Route>

              {/* Special handling: must change password redirects */}
              <Route path="*" element={<Landing />} />
            </Routes>
            </AppErrorBoundary>
            </DemoPreviewProvider>
          </AuthProvider>
        </LangProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
